import { AxiosResponse } from 'axios';
import { trainerApi } from './client';
import {
  Activity,
  ActivityAdjustment,
  ActivityAdjustmentRequest,
  ActivityLap,
  ActivityQuery,
  ActivitySample,
  ActivitySummaryQuery,
  ActivitySummaryResponse,
  BodyBatteryDay,
  DailyHealth,
  EffortLimit,
  GarminKeyRequest,
  GarminStatus,
  HealthRange,
  HealthResource,
  HrvDay,
  Page,
  RecordDistance,
  RecordEffortsResponse,
  RecordOverrideRequest,
  ProgressionResponse,
  RecordsResponse,
  RecordValue,
  SleepNight,
  SyncStatus,
  TrainerMe,
} from './types';

const data = <T>(response: AxiosResponse<T>): T => response.data;

// ---- Account -------------------------------------------------------------

/** GET /me. 401 when not logged in, 403 when this login has no trainer account. */
export const getMe = () => trainerApi.get<TrainerMe>('/me').then(data);

/** DELETE /me. Removes all of the account's data (soft-deletes the account row). 204. */
export const deleteMyAccount = () => trainerApi.delete<void>('/me').then(data);

export interface ExportFile {
  blob: Blob;
  filename: string;
}

/** GET /export. A JSON attachment. 413 when the account holds too much data for one file. */
export const exportMyData = async (): Promise<ExportFile> => {
  const response = await trainerApi.get<Blob>('/export', { responseType: 'blob' });
  const disposition = response.headers['content-disposition'] as string | undefined;
  const match = disposition?.match(/filename="?([^";]+)"?/i);
  return { blob: response.data, filename: match?.[1] ?? 'trainer-export.json' };
};

// ---- Garmin connection and sync -------------------------------------------

/** PUT /garmin/key. Validates the key with Garmin before storing it. Returns the new status. */
export const putGarminKey = (request: GarminKeyRequest) =>
  trainerApi.put<GarminStatus>('/garmin/key', request).then(data);

/** DELETE /garmin/key. Removes the key, the token and the credential. 204. */
export const deleteGarminKey = () => trainerApi.delete<void>('/garmin/key').then(data);

/** GET /garmin/status */
export const getGarminStatus = () => trainerApi.get<GarminStatus>('/garmin/status').then(data);

/**
 * POST /sync. Starts a background sync and answers 202 with the status so far. full=true fetches the whole history again.
 * Also gives activities whose file download failed new attempts.
 * 429 with Retry-After inside the cooldown. 409 when no valid key is connected or a job is already running. 503 when encryption is not configured.
 */
export const startSync = (options: { full?: boolean } = {}) =>
  trainerApi.post<SyncStatus>('/sync', null, options.full ? { params: { full: true } } : undefined).then(data);

/** GET /sync/status */
export const getSyncStatus = () => trainerApi.get<SyncStatus>('/sync/status').then(data);

// ---- Activities ------------------------------------------------------------

/** GET /activities (paged, newest first). */
export const getActivities = (query: ActivityQuery = {}) =>
  trainerApi.get<Page<Activity>>('/activities', { params: query }).then(data);

/** GET /activities/summary. Running activities only by default (the backend default types). */
export const getActivitySummary = (query: ActivitySummaryQuery) =>
  trainerApi.get<ActivitySummaryResponse>('/activities/summary', { params: query }).then(data);

/** GET /activities/{id}. A foreign or unknown id is 404. */
export const getActivity = (id: number) => trainerApi.get<Activity>(`/activities/${id}`).then(data);

/** GET /activities/{id}/laps. Empty until the FIT file is downloaded. */
export const getActivityLaps = (id: number) =>
  trainerApi.get<ActivityLap[]>(`/activities/${id}/laps`).then(data);

/** GET /activities/{id}/samples. Empty until the FIT file is downloaded. */
export const getActivitySamples = (id: number) =>
  trainerApi.get<ActivitySample[]>(`/activities/${id}/samples`).then(data);

/** PUT /activities/{id}/adjustment. Sets the official result for an activity (replaces an earlier one). */
export const putActivityAdjustment = (id: number, request: ActivityAdjustmentRequest) =>
  trainerApi.put<ActivityAdjustment>(`/activities/${id}/adjustment`, request).then(data);

/** DELETE /activities/{id}/adjustment. 204. */
export const deleteActivityAdjustment = (id: number) =>
  trainerApi.delete<void>(`/activities/${id}/adjustment`).then(data);

// ---- Health ----------------------------------------------------------------

/** Page size for health reads. The backend caps it at trainer.sync.max-page-size (100 by default). */
export const HEALTH_PAGE_SIZE = 100;
const MAX_HEALTH_PAGES = 5;

export interface HealthRows {
  daily: DailyHealth;
  sleep: SleepNight;
  hrv: HrvDay;
  'body-battery': BodyBatteryDay;
}

/**
 * Reads every page of one health resource for a date range (YYYY-MM-DD, both inclusive), oldest first.
 * The backend returns one row per day. The range is capped at 366 days by the backend.
 */
export async function getHealthRange<K extends HealthResource>(resource: K, range: HealthRange): Promise<HealthRows[K][]> {
  const all: HealthRows[K][] = [];
  for (let page = 0; page < MAX_HEALTH_PAGES; page++) {
    const result = await trainerApi
      .get<Page<HealthRows[K]>>(`/health/${resource}`, { params: { ...range, page, size: HEALTH_PAGE_SIZE } })
      .then(data);
    all.push(...result.items);
    if (page + 1 >= result.totalPages) break;
  }
  return all;
}

// ---- Records ---------------------------------------------------------------

/** GET /records. Every catalog distance, with effective, automatic and override values. */
export const getRecords = () => trainerApi.get<RecordsResponse>('/records').then(data);

/** PUT /records/{distance}. Sets a manual override (replaces an earlier one). 400 when the time is implausible. */
export const putRecordOverride = (distance: RecordDistance, request: RecordOverrideRequest) =>
  trainerApi.put<RecordValue>(`/records/${distance}`, request).then(data);

/** DELETE /records/{distance}. Removes the manual value; the automatic value shows again. 204. */
export const deleteRecordOverride = (distance: RecordDistance) =>
  trainerApi.delete<void>(`/records/${distance}`).then(data);

/**
 * GET /records/progression?top=N. Per distance: the top N efforts of each calendar year (fastest first) and the
 * all-time personal-best sequence. top is 1..10; 400 outside that range.
 */
export const getRecordsProgression = (top: number) =>
  trainerApi.get<ProgressionResponse>('/records/progression', { params: { top } }).then(data);

/**
 * POST /records/recompute. Starts the recompute in the background and answers 202 with the status (progress is in
 * job). 409 when a sync or recompute is already running.
 */
export const recomputeRecords = () => trainerApi.post<SyncStatus>('/records/recompute').then(data);

/** GET /records/{distance}/efforts. 404 for an unknown distance code. Running only, fastest first. */
export const getRecordEfforts = (distance: RecordDistance, limit: EffortLimit) =>
  trainerApi.get<RecordEffortsResponse>(`/records/${distance}/efforts`, { params: { limit } }).then(data);

// ---- Not in the backend -------------------------------------------------------
// There is no VO2max, training status or training load endpoint (the Garmin personal API does not expose them
// in this wrapper), and no weight endpoint. The Fitness page works from activities and health data only.
